import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  ReplaySubject,
  combineLatest,
  filter,
  map,
  scan,
  shareReplay,
  startWith,
  tap,
} from 'rxjs';
import { ISavedQuote } from '../models/savedQuotes.models';

type QuoteEvent =
  | { type: 'deleted'; savedQuoteId: string }
  | { type: 'updated'; quote: ISavedQuote };

@Injectable()
export class SavedQuotesService {
  private readonly baseUrl = '/transcripts';

  private quotes$ = new ReplaySubject<ISavedQuote[]>();
  private quotesInner$ = this.quotes$.pipe(
    scan<ISavedQuote[], ISavedQuote[]>((prev, curr) => [...prev, ...curr], []),
    startWith([])
  );

  private quoteEvents$ = new ReplaySubject<QuoteEvent>();

  private updatedQuotes$ = this.quoteEvents$.pipe(
    filter(
      (event): event is Extract<QuoteEvent, { type: 'updated' }> =>
        event.type === 'updated'
    ),
    scan<Extract<QuoteEvent, { type: 'updated' }>, Record<string, ISavedQuote>>(
      (prev, { quote }) => ({ ...prev, [quote.savedQuoteId]: quote }),
      {}
    ),
    map((data) => Object.values(data)),
    startWith([])
  );

  private deletedQuoteIds$ = this.quoteEvents$.pipe(
    scan<QuoteEvent, string[]>(
      (prev, event) =>
        event.type === 'deleted'
          ? [...prev, event.savedQuoteId]
          : prev.filter((id) => id !== event.quote.savedQuoteId),
      []
    ),
    startWith([])
  );

  private initialSavedQuoteIds$ = new BehaviorSubject<Set<string>>(new Set());

  allQuotes$ = combineLatest([
    this.quotesInner$,
    this.updatedQuotes$,
    this.deletedQuoteIds$,
  ]).pipe(
    map(([quotes, update, deletedQuoteIds]) =>
      this.joinQuotes(quotes, deletedQuoteIds, update)
    ),
    startWith<Record<string, ISavedQuote[]>>({})
  );

  savedQuoteIds$ = combineLatest([
    this.allQuotes$,
    this.initialSavedQuoteIds$,
    this.deletedQuoteIds$,
  ]).pipe(
    map(([allQuotes, initialIds, deletedQuoteIds]) => {
      const savedQuoteIds = new Set(initialIds);

      Object.values(allQuotes)
        .flat()
        .forEach((quote) => savedQuoteIds.add(quote.savedQuoteId));

      deletedQuoteIds.forEach((quoteId) => savedQuoteIds.delete(quoteId));

      return savedQuoteIds;
    }),
    shareReplay(1)
  );

  constructor(protected http: HttpClient) {}

  initializeSavedQuoteIds(savedQuoteIds: string[]): void {
    const updatedIds = new Set(this.initialSavedQuoteIds$.value);

    savedQuoteIds.forEach((id) => updatedIds.add(id));

    this.initialSavedQuoteIds$.next(updatedIds);
  }

  saveQuote(quoteId: string) {
    return this.http
      .post<ISavedQuote>(`${this.baseUrl}/quotes/${quoteId}`, {})
      .pipe(
        tap((quote) => this.quoteEvents$.next({ type: 'updated', quote }))
      );
  }

  deleteQuote(quoteId: string) {
    return this.http.delete<void>(`${this.baseUrl}/quotes/${quoteId}`).pipe(
      tap(() => {
        this.quoteEvents$.next({ type: 'deleted', savedQuoteId: quoteId });
      })
    );
  }

  getQuotesByExpertId(expertId: string) {
    return this.http
      .get<ISavedQuote[]>(`${this.baseUrl}/quotes?expertId=${expertId}`)
      .pipe(tap((quotes) => this.quotes$.next(quotes)));
  }

  private joinQuotes(
    quotes: ISavedQuote[],
    deletedQuoteIds: string[],
    update: ISavedQuote[]
  ) {
    const quoteMap = quotes.reduce<Record<string, ISavedQuote>>(
      (prev, curr) => ({ ...prev, [curr.savedQuoteId]: curr }),
      {}
    );

    return [
      ...Object.values(quoteMap).filter((q) =>
        update.every((u) => u.savedQuoteId !== q.savedQuoteId)
      ),
      ...update,
    ]
      .filter((q) => !deletedQuoteIds.includes(q.savedQuoteId))
      .reduce<Record<string, ISavedQuote[]>>(
        (prev, curr) => ({
          ...prev,
          [curr.expertId]: [...(prev[curr.expertId] || []), curr],
        }),
        {}
      );
  }
}
